import { useCallback, useEffect, useMemo, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { useParams } from 'react-router-dom';
import {
  DetailPanelGrid,
  SingleValuePanel,
  TableActionMenu,
  useBackofficePageMode,
  useCustomizableDetailTabs,
  panelSpanFromLayout,
} from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import {
  clearMessageBusCache,
  fetchMessageBusSubscriber,
  fetchMessageBusSubscriberMessages,
  manageMessageBusDeadLetter,
} from './api';
import {
  ManageDeadLetterMessagesModal,
  MessageBusMessagePreviewTile,
  MessageBusMessagesTableTile,
  MessageBusPageHeader,
  MessageBusSubscriberDetailsTile,
} from './components';
import type {
  BackofficeMessageBusDeadLetterAction,
  BackofficeMessageBusPeekedMessage,
  BackofficeMessageBusSubscriberDetail,
  BackofficeMessageBusSubscriberMessagesResponse,
} from './types';

const TABS: Array<{ id: 'subscriber'; label: string }> = [
  { id: 'subscriber', label: 'Subscriber' },
];
const TABS_PREFERENCE_SCOPE = 'messagebus:subscriber:tabs';
const LAYOUT_PREFERENCE_SCOPE = 'messagebus:subscriber:layout';
const DEFAULT_TILE_IDS = [
  'current',
  'active',
  'dead-letter',
  'total',
  'details',
  'messages',
  'preview',
];

type ManageDeadLetterScope = 'queue' | 'selected';

function getDefaultBatchCount(deadLetterMessageCount: number): string {
  return deadLetterMessageCount > 0 ? String(Math.min(deadLetterMessageCount, 100)) : '';
}

function parseValidBatchCount(input: string, max: number): number | null {
  if (max <= 0) {
    return null;
  }

  const trimmed = input.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }

  const value = Number(trimmed);
  if (!Number.isSafeInteger(value) || value < 1 || value > max) {
    return null;
  }

  return value;
}

export function BackofficeMessageBusSubscriberPage() {
  const { topicName = '', subscriberName = '' } = useParams<{
    topicName: string;
    subscriberName: string;
  }>();
  const decodedTopicName = decodeURIComponent(topicName);
  const decodedSubscriberName = decodeURIComponent(subscriberName);
  useBackofficePageTitle(`Subscriber: ${decodedSubscriberName}`);
  const { pageMode, setPageMode } = useBackofficePageMode();
  const [data, setData] = useState<BackofficeMessageBusSubscriberDetail | null>(null);
  const [messagesData, setMessagesData] =
    useState<BackofficeMessageBusSubscriberMessagesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [manageScope, setManageScope] = useState<ManageDeadLetterScope | null>(null);
  const [manageError, setManageError] = useState<string | null>(null);
  const [manageSuccess, setManageSuccess] = useState<string | null>(null);
  const [manageWarning, setManageWarning] = useState<string | null>(null);
  const [batchCountInput, setBatchCountInput] = useState('');
  const [selectedMessageKeys, setSelectedMessageKeys] = useState<Set<string>>(() => new Set());
  const [modalSelectedMessages, setModalSelectedMessages] = useState<
    BackofficeMessageBusPeekedMessage[]
  >([]);
  const [selectedActionCompleted, setSelectedActionCompleted] = useState(false);
  const [submittingAction, setSubmittingAction] =
    useState<BackofficeMessageBusDeadLetterAction | null>(null);
  const tabs = useCustomizableDetailTabs<'subscriber'>({
    preferenceScope: TABS_PREFERENCE_SCOPE,
    builtInTabs: TABS,
    defaultTabId: 'subscriber',
    basePath: `/developer/messagebus/topics/${encodeURIComponent(decodedTopicName)}/subscribers/${encodeURIComponent(decodedSubscriberName)}`,
    pageMode,
  });
  const activeCustomTab = tabs.activeCustomTab;
  const layoutPreferenceScope = activeCustomTab
    ? `${LAYOUT_PREFERENCE_SCOPE}:${activeCustomTab.id}`
    : LAYOUT_PREFERENCE_SCOPE;
  const defaultVisibleTileIds = activeCustomTab ? [] : DEFAULT_TILE_IDS;
  const showTabs = pageMode === 'edit' || tabs.customTabs.length > 0;

  const load = useCallback(
    (signal?: AbortSignal, forceRefresh = false) => {
      setLoading(true);
      setError(null);
      return Promise.all([
        fetchMessageBusSubscriber(decodedTopicName, decodedSubscriberName, signal, forceRefresh),
        fetchMessageBusSubscriberMessages(
          decodedTopicName,
          decodedSubscriberName,
          'dead-letter',
          signal,
          forceRefresh
        ),
      ])
        .then(([subscriber, messages]) => {
          setData(subscriber);
          setMessagesData(messages);
          const availableMessageKeys = new Set(
            messages.messages.map((message) => message.messageKey)
          );
          setSelectedMessageKeys((current) => {
            const next = new Set(
              Array.from(current).filter((messageKey) => availableMessageKeys.has(messageKey))
            );
            return next.size === current.size ? current : next;
          });
        })
        .catch((caught: unknown) => {
          if (!(caught instanceof DOMException && caught.name === 'AbortError')) {
            setError(caught instanceof Error ? caught.message : 'Failed to load subscriber');
          }
        })
        .finally(() => {
          if (!signal?.aborted) {
            setLoading(false);
          }
        });
    },
    [decodedSubscriberName, decodedTopicName]
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const refresh = useCallback(() => {
    setRefreshing(true);
    void load(undefined, true).finally(() => setRefreshing(false));
  }, [load]);

  useEffect(() => {
    if (manageScope !== 'queue' || !data) {
      return;
    }

    const parsedCount = parseValidBatchCount(batchCountInput, data.deadLetterMessageCount);
    if (parsedCount !== null) {
      return;
    }

    setBatchCountInput(getDefaultBatchCount(data.deadLetterMessageCount));
  }, [batchCountInput, data, manageScope]);

  const handleManageDeadLetterMessages = useCallback(
    async (action: BackofficeMessageBusDeadLetterAction) => {
      if (!data) {
        return;
      }

      const request = (() => {
        if (manageScope === 'selected') {
          return {
            action,
            messageKeys: modalSelectedMessages.map((message) => message.messageKey),
          } as const;
        }

        const batchCount = parseValidBatchCount(batchCountInput, data.deadLetterMessageCount);
        if (batchCount === null) {
          return null;
        }

        return { action, count: batchCount } as const;
      })();
      if (!request) {
        setManageSuccess(null);
        setManageWarning(null);
        setManageError('Enter a batch size between 1 and the current dead-letter count.');
        return;
      }

      setManageError(null);
      setManageSuccess(null);
      setManageWarning(null);
      setSubmittingAction(action);

      try {
        const response = await manageMessageBusDeadLetter(
          decodedTopicName,
          decodedSubscriberName,
          request
        );
        clearMessageBusCache();
        await load(undefined, true);
        if (manageScope === 'selected') {
          setSelectedMessageKeys(new Set());
          setSelectedActionCompleted(true);
          if (response.processedCount > 0) {
            setManageSuccess(
              `${action === 'purge' ? 'Purged' : 'Resent'} ${response.processedCount} selected message${response.processedCount === 1 ? '' : 's'}.`
            );
          }
          if (response.unmatchedMessageKeys.length > 0) {
            setManageWarning(
              `${response.unmatchedMessageKeys.length} selected message${response.unmatchedMessageKeys.length === 1 ? ' was' : 's were'} no longer available.`
            );
          }
        } else {
          setManageSuccess(
            `${action === 'purge' ? 'Purged' : 'Re-queued'} ${response.processedCount} dead-letter message${response.processedCount === 1 ? '' : 's'}.`
          );
        }
      } catch (caught: unknown) {
        setManageError(
          caught instanceof Error ? caught.message : 'Failed to manage dead-letter messages'
        );
      } finally {
        setSubmittingAction(null);
      }
    },
    [
      batchCountInput,
      data,
      decodedSubscriberName,
      decodedTopicName,
      load,
      manageScope,
      modalSelectedMessages,
    ]
  );

  const handleOpenManageModal = useCallback(() => {
    setManageError(null);
    setManageSuccess(null);
    setManageWarning(null);
    setSelectedActionCompleted(false);
    setModalSelectedMessages([]);
    setBatchCountInput(getDefaultBatchCount(data?.deadLetterMessageCount ?? 0));
    setManageScope('queue');
  }, [data?.deadLetterMessageCount]);

  const handleOpenSelectedManageModal = useCallback(() => {
    const selectedMessages = (messagesData?.messages ?? []).filter((message) =>
      selectedMessageKeys.has(message.messageKey)
    );
    if (selectedMessages.length === 0) {
      return;
    }

    setManageError(null);
    setManageSuccess(null);
    setManageWarning(null);
    setSelectedActionCompleted(false);
    setModalSelectedMessages(selectedMessages);
    setManageScope('selected');
  }, [messagesData?.messages, selectedMessageKeys]);

  const batchCountDisabled = useMemo(() => {
    if (!data) {
      return true;
    }

    return parseValidBatchCount(batchCountInput, data.deadLetterMessageCount) === null;
  }, [batchCountInput, data]);

  const items = useMemo(
    () => [
      {
        id: 'current',
        title: 'Current messages',
        layout: { w: 2, h: 1, minW: 1, minH: 1, maxW: 4, maxH: 2, isResizable: true },
        render: () =>
          data ? (
            <SingleValuePanel
              title="Current messages"
              value={String(data.currentMessageCount)}
              footer="active + dead-letter"
              panelId="current"
            />
          ) : null,
      },
      {
        id: 'active',
        title: 'Active messages',
        layout: { w: 2, h: 1, minW: 1, minH: 1, maxW: 4, maxH: 2, isResizable: true },
        render: () =>
          data ? (
            <SingleValuePanel
              title="Active messages"
              value={String(data.activeMessageCount)}
              footer="primary queue"
              panelId="active"
            />
          ) : null,
      },
      {
        id: 'dead-letter',
        title: 'Dead letters',
        layout: { w: 2, h: 1, minW: 1, minH: 1, maxW: 4, maxH: 2, isResizable: true },
        render: () =>
          data ? (
            <SingleValuePanel
              title="Dead letters"
              value={String(data.deadLetterMessageCount)}
              footer="dead-letter queue"
              valueTone="neutral"
              panelId="dead-letter"
              headerActions={
                <TableActionMenu
                  label="Dead-letter tile actions"
                  actions={[
                    {
                      id: 'manage-dead-letter',
                      label: 'Manage messages',
                      icon: SlidersHorizontal,
                      placement: 'menu',
                      onSelect: handleOpenManageModal,
                    },
                  ]}
                />
              }
            />
          ) : null,
      },
      {
        id: 'total',
        title: 'Total messages',
        layout: { w: 2, h: 1, minW: 1, minH: 1, maxW: 4, maxH: 2, isResizable: true },
        render: () =>
          data ? (
            <SingleValuePanel
              title="Total messages"
              value={String(data.totalMessageCount)}
              footer="broker-reported total"
              panelId="total"
            />
          ) : null,
      },
      {
        id: 'details',
        title: 'Subscriber details',
        layout: { w: 4, h: 4, minW: 1, minH: 1, maxW: 8, maxH: 5, isResizable: true },
        render: (layout: Parameters<typeof panelSpanFromLayout>[0]) =>
          data ? (
            <MessageBusSubscriberDetailsTile
              subscriber={data}
              panelId="details"
              currentSpan={panelSpanFromLayout(layout, '3x3')}
            />
          ) : null,
      },
      {
        id: 'messages',
        title: 'DLQ messages',
        layout: { w: 8, h: 4, minW: 4, minH: 2, maxW: 12, maxH: 6, isResizable: true },
        render: () =>
          data ? (
            <MessageBusMessagesTableTile
              title="DLQ messages"
              topicName={data.topicName}
              subscriberName={data.subscriberName}
              sourceLabel="Showing the first 50 dead-letter messages"
              messages={messagesData?.messages ?? []}
              emptyLabel="No dead-letter messages available to preview."
              panelId="messages"
              span="4x3"
              selectedMessageKeys={selectedMessageKeys}
              onSelectedMessageKeysChange={setSelectedMessageKeys}
              onManageSelected={handleOpenSelectedManageModal}
            />
          ) : null,
      },
      {
        id: 'preview',
        title: 'Message preview',
        layout: { w: 4, h: 4, minW: 3, minH: 2, maxW: 8, maxH: 6, isResizable: true },
        render: () => (
          <MessageBusMessagePreviewTile
            title="Message preview"
            sourceLabel="DLQ"
            panelId="preview"
            span="4x3"
          />
        ),
      },
    ],
    [
      data,
      handleOpenManageModal,
      handleOpenSelectedManageModal,
      messagesData?.messages,
      selectedMessageKeys,
    ]
  );

  return (
    <main className="min-h-[320px] bg-white p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        {/* extracted to keep message-bus page actions aligned across overview, topic, and subscriber */}
        <MessageBusPageHeader
          showTabs={showTabs}
          tabs={tabs}
          pageLabel="subscriber page"
          pageMode={pageMode}
          onPageModeChange={setPageMode}
          onRefresh={refresh}
          refreshing={refreshing}
        />
        {loading && !data ? (
          <p className="text-body text-neutral-500">Loading subscriber…</p>
        ) : null}
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-body text-red-700">
            {error}
          </div>
        ) : null}
        {data ? (
          <DetailPanelGrid
            resetKey={tabs.activeTabId}
            preferenceScope={layoutPreferenceScope}
            layoutMode={pageMode}
            cols={{ xxl: 12, lg: 12, md: 6, sm: 4, xs: 1, xxs: 1 }}
            defaultVisibleItemIds={defaultVisibleTileIds}
            addTileLayout={{ w: 2, h: 1 }}
            tileCatalogLabel="Add subscriber tile"
            items={items}
          />
        ) : null}
        {manageScope && data ? (
          <ManageDeadLetterMessagesModal
            topicName={data.topicName}
            subscriberName={data.subscriberName}
            deadLetterMessageCount={data.deadLetterMessageCount}
            batchCountInput={batchCountInput}
            successMessage={manageSuccess}
            warningMessage={manageWarning}
            error={manageError}
            submittingAction={submittingAction}
            batchCountDisabled={batchCountDisabled}
            scope={manageScope}
            selectedMessages={modalSelectedMessages}
            actionCompleted={selectedActionCompleted}
            onClose={() => {
              if (!submittingAction) {
                setManageScope(null);
              }
            }}
            onBatchCountInputChange={(value) => {
              setManageSuccess(null);
              setManageError(null);
              setBatchCountInput(value);
            }}
            onSubmit={handleManageDeadLetterMessages}
          />
        ) : null}
      </div>
    </main>
  );
}
